import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropIcon, DeleteIcon } from 'assests/GmsIcons';
import { SortOptionRowProps, TProductSortingItem, TSecondarySortOption } from '../model';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import { SecondarySortSelect } from './SecondarySortSelect';
import { TypeBadge } from './TypeBadge';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';

const COMPONENT_ID = 'sort-option-row';
const TITLE_SYNC_DEBOUNCE_MS = 500;

export const SortOptionRow = ({
    sortOption,
    index,
    onVisibilityChange,
    onSecondarySortChange,
    onTitleChange,
    onDelete,
    secondarySortOptions,
    onDragStart,
    onDragOver,
    onDragEnd,
    isDragging,
    dragOverIndex,
}: SortOptionRowProps) => {
    const {
        primary_sort_id,
        primary_sort_title,
        sort_type,
        is_visible,
        secondary_sort_id,
        tool_tip_message,
        sort_order,
    } = sortOption;

    const [localTitle, setLocalTitle] = useState(primary_sort_title);
    const isEditingRef = useRef(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!isEditingRef.current) {
            setLocalTitle(primary_sort_title);
        }
    }, [primary_sort_title]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const syncTitleToParent = useCallback((title: string) => {
        isEditingRef.current = false;
        if (title !== primary_sort_title && onTitleChange) {
            onTitleChange(primary_sort_id, sort_order, title);
        }
    }, [primary_sort_id, sort_order, primary_sort_title, onTitleChange]);

    const handleTitleChange = useCallback((value: string) => {
        setLocalTitle(value);
        isEditingRef.current = true;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            syncTitleToParent(value);
        }, TITLE_SYNC_DEBOUNCE_MS);
    }, [syncTitleToParent]);

    const handleTitleBlur = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        syncTitleToParent(localTitle);
    }, [localTitle, syncTitleToParent]);

    const canDelete = sort_type === 'TEXT' || sort_type === 'INTEGER' || sort_type === 'DECIMAL';

    const handleSecondarySortChange = (selectedId: string | number) => {
        const selectedOption = secondarySortOptions.find((opt) => opt.id === selectedId);
        const selectedTitle = selectedOption?.label ?? '';
        onSecondarySortChange(primary_sort_id, sort_order, selectedId as string, selectedTitle);
    };

    const handleDeleteClick = () => {
        if (onDelete && canDelete) {
            onDelete(primary_sort_id);
        }
    };

    return (
        <tr
            id={`${COMPONENT_ID}-${primary_sort_id}`}
            data-testid={`${COMPONENT_ID}-${index}`}
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragOver={(e) => onDragOver(e, index)}
            onDragEnd={onDragEnd}
            className={`
                tw-border-b tw-border-edge
                ${isDragging ? 'tw-opacity-50' : ''}
                ${index % 2 === 0 ? 'tw-bg-surface-sunken' : 'tw-bg-surface'}
                ${dragOverIndex === index ? 'tw-border-t-2 tw-border-t-accent' : ''}
                hover:tw-bg-surface-hover tw-transition-colors
            `}
        >
            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-drag-handle`}
                data-testid={`${COMPONENT_ID}-${index}-drag-handle`}
                className="tw-px-4 tw-py-3 tw-w-12"
            >
                <div className="tw-cursor-grab active:tw-cursor-grabbing tw-text-ink-tertiary hover:tw-text-ink">
                    <DragDropIcon />
                </div>
            </td>

            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-title-cell`}
                data-testid={`${COMPONENT_ID}-${index}-title-cell`}
                className="tw-px-4 tw-py-3 tw-w-full"
            >
                <div className="tw-flex tw-items-center tw-gap-4">
                    <TextField
                        data-testid={`${COMPONENT_ID}-${index}-title-input`}
                        label=""
                        placeholder="Enter text"
                        value={localTitle}
                        onChange={handleTitleChange}
                        onBlur={handleTitleBlur}
                        aria-label={`Sort option title: ${primary_sort_title}`}
                        className="tw-w-full"
                    />
                    {/* DS InfoTooltip, was `Rules/Components/OverlayIcon` — same
                        placement/message API, but the glyph follows ink tokens instead
                        of OverlayIcon's hardcoded `fill="#A7A9AF"`. */}
                    <InfoTooltip
                        placement="top"
                        message={
                            <div>
                                {tool_tip_message.includes('Default Title') ? (
                                    <>
                                        <p>{tool_tip_message.split('Default Title')[0].trim()}</p>
                                        <p>Default Title{tool_tip_message.split('Default Title')[1]}</p>
                                    </>
                                ) : (
                                    <p>{tool_tip_message}</p>
                                )}
                            </div>
                        }
                    />
                </div>
            </td>

            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-type-cell`}
                data-testid={`${COMPONENT_ID}-${index}-type-cell`}
                className="tw-px-4 tw-py-3 tw-text-start"
            >
                <TypeBadge id={primary_sort_id} type={sort_type} />
            </td>

            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-visible-cell`}
                data-testid={`${COMPONENT_ID}-${index}-visible-cell`}
                className="tw-px-4 tw-py-3 tw-text-center"
            >
                <Toggle
                    label=""
                    checked={is_visible}
                    onChange={(checked) => onVisibilityChange(primary_sort_id, sort_order, checked)}
                />
            </td>

            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-secondary-sort-cell`}
                data-testid={`${COMPONENT_ID}-${index}-secondary-sort-cell`}
                className="tw-px-4 tw-py-3"
            >
                <SecondarySortSelect
                    id={primary_sort_id}
                    value={secondary_sort_id}
                    options={secondarySortOptions}
                    onChange={handleSecondarySortChange}
                />
            </td>

            <td
                id={`${COMPONENT_ID}-${primary_sort_id}-delete-cell`}
                data-testid={`${COMPONENT_ID}-${index}-delete-cell`}
                /* Matches the header's `tw-w-20`. Under `table-fixed` only the FIRST row
                   sets the column widths, so this is inert — but a body cell claiming a
                   different width than its own header is a trap for the next reader. */
                className="tw-px-4 tw-py-3 tw-w-20"
            >
                {canDelete && (
                    <Button
                        id={`${COMPONENT_ID}-${primary_sort_id}-delete-btn`}
                        data-testid={`${COMPONENT_ID}-${index}-delete-btn`}
                        type="button"
                        variant="subtle"
                        size="sm"
                        icon={<DeleteIcon />}
                        showIcon
                        onClick={handleDeleteClick}
                        aria-label={`Delete ${primary_sort_title}`}
                    >
                        {""}
                    </Button>
                )}
            </td>
        </tr>
    );
};